import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { setSessionToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { FlaskConical } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        // Persist token as fallback for browsers that drop our cookie
        // (iOS Safari ITP, Chrome-on-iOS, Incognito, etc.)
        if (data.session_token) setSessionToken(data.session_token);
        setUser(data.user);
        trackEvent("login", { method: "google" });
        // Remove hash
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true, state: { user: data.user } });
      } catch (err) {
        // Fallback: maybe the session_id was already exchanged on a previous
        // attempt and the cookie is valid. Check /auth/me before giving up.
        try {
          const me = await api.get("/auth/me");
          if (me?.data?.user_id) {
            setUser(me.data);
            window.history.replaceState(null, "", window.location.pathname);
            navigate("/dashboard", { replace: true, state: { user: me.data } });
            return;
          }
        } catch (_) { /* fall through */ }
        console.error("Auth callback failed:", err);
        const detail = err?.response?.data?.detail;
        if (detail) {
          window.sessionStorage.setItem("auth_error", detail);
        }
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3" data-testid="auth-callback">
        <FlaskConical className="h-6 w-6 animate-pulse text-blue-600" />
        <span className="text-sm text-zinc-500 font-mono">signing you in…</span>
      </div>
    </div>
  );
}
